import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FloatingField,
} from '@/shared/ui'

import { reauthenticate } from '../model/session-lifecycle'

import '../i18n'

/**
 * goauth guards its account-destroying endpoints with a sudo window: the token
 * must have been minted from a password within `ADMIN_REAUTH_WINDOW`. Only the
 * password grant resets `auth_time` — a refresh deliberately does not, and
 * there is no dedicated re-authentication endpoint — so satisfying it means
 * signing in again behind the scenes.
 *
 * The caller keeps the action that was refused and replays it via `onConfirmed`.
 */
export function SudoDialog({
  open,
  onOpenChange,
  onConfirmed,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirmed: () => void
}) {
  const { t } = useTranslation(['auth', 'common'])
  const [password, setPassword] = useState('')
  const [isPending, setPending] = useState(false)
  const [hasFailed, setFailed] = useState(false)

  const close = (next: boolean) => {
    if (!next) {
      setPassword('')
      setFailed(false)
    }
    onOpenChange(next)
  }

  const confirm = async () => {
    setPending(true)
    setFailed(false)

    const result = await reauthenticate(password)

    setPending(false)
    if (!result.ok) {
      setFailed(true)
      return
    }

    setPassword('')
    onOpenChange(false)
    onConfirmed()
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-modal-confirm">
        <DialogHeader>
          <DialogTitle>{t('sudo.title')}</DialogTitle>
          <DialogDescription>{t('sudo.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-5">
          {hasFailed && (
            <Alert variant="destructive">
              <AlertDescription>{t('sudo.failed')}</AlertDescription>
            </Alert>
          )}

          <FloatingField
            label={t('sudo.password')}
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && password !== '') void confirm()
            }}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="default" isLoading={isPending} onClick={() => void confirm()}>
            {t('sudo.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
